import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { sendChatMessage } from "@/lib/openai";
import { useToast } from "@/hooks/use-toast";

export function AIAssistant({ sidebarExpanded = true }: { sidebarExpanded?: boolean }) {
  const [compactOpen, setCompactOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    setIsLoading(true);
    try {
      // Notify AI control of user activity to reset auto-shutdown timer
      window.dispatchEvent(new CustomEvent('ai-activity'));
      console.log("Sending message to AI:", message);
      const response = await sendChatMessage(message);
      console.log("AI Response received:", response);
      
      // Show a toast with the AI response
      if (response && response !== "Sorry, I couldn't process your request at this time.") {
        toast({
          title: "AI Assistant",
          description: response.substring(0, 500) + (response.length > 500 ? "..." : ""),
          duration: 10000,
        });
      } else {
        // Show the response in an alert if it's an error
        alert(`AI Response: ${response || "Empty response"}`);
      }
      
      setMessage("");
    } catch (error) {
      console.error("Error sending message to AI:", error);
      toast({
        title: "Error",
        description: "Failed to get a response from the AI assistant. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {!compactOpen && <Button
        aria-label="Open assistant"
        aria-expanded={false}
        onClick={() => setCompactOpen(true)}
        className="hidden [@media(max-height:499px)]:inline-flex fixed bottom-3 right-3 z-40 rounded-full"
      >Assistant</Button>}
    <div data-assistant-bar className={`${compactOpen ? "" : "[@media(max-height:499px)]:hidden"} fixed bottom-0 left-0 ${sidebarExpanded ? "lg:left-64" : ""} right-0 bg-white dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700 p-4 shadow-lg transition-all duration-300 z-40`}>
      <div className="container mx-auto max-w-7xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <span className="mdi mdi-robot text-white text-xl"></span>
          </div>
          <div className="flex-1">
            <Input
              aria-label="Assistant message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me about your performance data, meeting summaries, or coaching suggestions..."
              className="w-full"
              disabled={isLoading}
            />
          </div>
          <Button 
            aria-label="Send assistant message"
            onClick={handleSendMessage}
            disabled={isLoading || !message.trim()}
            size="icon"
          >
            <span className="mdi mdi-send"></span>
          </Button>
          <Button variant="ghost" aria-label="Close assistant"
            className="hidden [@media(max-height:499px)]:inline-flex"
            onClick={() => setCompactOpen(false)}>Close</Button>
        </div>
      </div>
    </div>
    </>
  );
}
